import { useSharedPluginState } from '@atlaskit/editor-common/hooks';
import { textColorPalette } from '@atlaskit/editor-common/ui-color';
import {
  Toolbar,
  ToolbarSection,
  ToolbarTooltip,
  ToolbarUIProvider,
  SeparatorPosition,
} from '@atlaskit/editor-toolbar';
import { Pressable, Text, xcss } from '@atlaskit/primitives';
import { messages } from '@databases/intl/messages';
import React, { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl-next';
import { useSnapshot } from 'valtio';

import { currentThemeState } from '@canvas/canvas-state/theme';

import { type DatabasesEditorAPI } from '../databases-preset';

import { ColorPickerSection } from './color-picker-section';
import { ListSection } from './list-section';
import { TextFormattingSection } from './text-formatting-section';
import { noop } from './toolbar-constants';

const askRovoButtonStyles = xcss({
  display: 'flex',
  alignItems: 'center',
  gap: 'space.075',
  paddingBlock: 'space.050',
  paddingInline: 'space.100',
  borderWidth: 'border.width.0',
  borderRadius: 'border.radius',
  backgroundColor: 'color.background.neutral.subtle',
  cursor: 'pointer',
  font: 'font.body',
  fontWeight: 'font.weight.regular',
  color: 'color.text',
  whiteSpace: 'nowrap',
  ':hover': {
    backgroundColor: 'color.background.neutral.subtle.hovered',
  },
});

export interface DatabasesFormattingToolbarProps {
  editorApi: DatabasesEditorAPI;
  onAskRovo?: () => void;
  askRovoIcon?: React.ReactNode;
  onDropdownOpenChange?: (isOpen: boolean) => void;
}

export const DatabasesFormattingToolbar: React.FC<DatabasesFormattingToolbarProps> = ({
  editorApi,
  onAskRovo,
  askRovoIcon,
  onDropdownOpenChange,
}) => {
  const { formatMessage } = useIntl();
  const {
    textFormattingState,
    listState,
    textColorState: rawTextColorState,
    highlightState,
  } = useSharedPluginState(editorApi, [
    'textFormatting',
    'list',
    'textColor',
    'highlight',
  ]);

  // In dark mode the default text color (N800 / #172B4D) is invisible against
  // dark backgrounds. Override both `defaultColor` and `color` (when it matches
  // the original default) to the white entry from the text color palette.
  const { theme } = useSnapshot(currentThemeState);
  const isDarkMode = theme === 'dark';
  const darkModeDefaultColor = useMemo(
    () => textColorPalette.find((c) => c.label === 'White')?.value,
    []
  );
  const textColorState = useMemo(() => {
    if (!rawTextColorState || !isDarkMode || !darkModeDefaultColor) {
      return rawTextColorState;
    }
    const isUsingDefault =
      !rawTextColorState.color ||
      rawTextColorState.color === rawTextColorState.defaultColor;
    return {
      ...rawTextColorState,
      defaultColor: darkModeDefaultColor,
      ...(isUsingDefault ? { color: darkModeDefaultColor } : {}),
    };
  }, [rawTextColorState, isDarkMode, darkModeDefaultColor]);

  const [textStylesOpen, setTextStylesOpen] = useState(false);
  const [listMenuOpen, setListMenuOpen] = useState(false);
  const [colorPickerOpen, setColorPickerOpen] = useState(false);

  const execute = useMemo(() => editorApi?.core?.actions.execute, [editorApi]);

  const notifyDropdownChange = useCallback(
    (isOpen: boolean) => {
      onDropdownOpenChange?.(isOpen);
    },
    [onDropdownOpenChange]
  );

  const toggleTextStyles = useCallback(() => {
    setTextStylesOpen((prev) => {
      const next = !prev;
      setListMenuOpen(false);
      setColorPickerOpen(false);
      notifyDropdownChange(next);
      return next;
    });
  }, [notifyDropdownChange]);

  const toggleListMenu = useCallback(() => {
    setListMenuOpen((prev) => {
      const next = !prev;
      setTextStylesOpen(false);
      setColorPickerOpen(false);
      notifyDropdownChange(next);
      return next;
    });
  }, [notifyDropdownChange]);

  const toggleColorPicker = useCallback(() => {
    setColorPickerOpen((prev) => {
      const next = !prev;
      setTextStylesOpen(false);
      setListMenuOpen(false);
      notifyDropdownChange(next);
      return next;
    });
  }, [notifyDropdownChange]);

  const preventBlur: React.MouseEventHandler = useCallback((e) => {
    e.preventDefault();
  }, []);

  const closeTextStyles = useCallback(() => {
    setTextStylesOpen(false);
    notifyDropdownChange(false);
  }, [notifyDropdownChange]);

  const closeListMenu = useCallback(() => {
    setListMenuOpen(false);
    notifyDropdownChange(false);
  }, [notifyDropdownChange]);

  const closeColorPicker = useCallback(() => {
    setColorPickerOpen(false);
    notifyDropdownChange(false);
  }, [notifyDropdownChange]);

  return (
    <ToolbarUIProvider onDropdownOpenChanged={noop} preventDefaultOnMouseDown>
      <Toolbar label="Rich text formatting" testId="databases-formatting-toolbar">
        {/* Section 1: Ask Rovo */}
        {onAskRovo && (
          <ToolbarSection hasSeparator={SeparatorPosition.END}>
            <ToolbarTooltip
              content={formatMessage(messages['rich-text-formatting-toolbar.ask-rovo'])}
              position="top"
            >
              <Pressable
                xcss={askRovoButtonStyles}
                onClick={onAskRovo}
                onMouseDown={preventBlur}
                testId="ask-rovo-button"
              >
                {askRovoIcon}
                <Text>
                  {formatMessage(messages['rich-text-formatting-toolbar.ask-rovo'])}
                </Text>
              </Pressable>
            </ToolbarTooltip>
          </ToolbarSection>
        )}

        {/* Section 2: Text formatting */}
        <TextFormattingSection
          editorApi={editorApi}
          textFormattingState={textFormattingState}
          textColorState={textColorState}
          highlightState={highlightState}
          execute={execute}
          isOpen={textStylesOpen}
          onToggle={toggleTextStyles}
          onClose={closeTextStyles}
          preventBlur={preventBlur}
        />

        {/* Section 3: Lists */}
        <ListSection
          editorApi={editorApi}
          listState={listState}
          execute={execute}
          isOpen={listMenuOpen}
          onToggle={toggleListMenu}
          onClose={closeListMenu}
          preventBlur={preventBlur}
        />

        {/* Section 4: Text color & Highlight */}
        <ColorPickerSection
          editorApi={editorApi}
          textColorState={textColorState}
          highlightState={highlightState}
          execute={execute}
          isOpen={colorPickerOpen}
          onToggle={toggleColorPicker}
          onClose={closeColorPicker}
          preventBlur={preventBlur}
        />
      </Toolbar>
    </ToolbarUIProvider>
  );
};
